import type { MapType } from '@racemap/utilities/consts/events';
import { isEditableGeoElement } from '@racemap/utilities/functions/validation';
import type { EditMode } from '@racemap/utilities/types/geos';
import { type Immutable, produce } from 'immer';
import type { ViewState } from 'react-map-gl/mapbox';
import type { StoreApi } from 'zustand';
import type { Selection } from '../../components/BasicComponents/Maps/Editor';
import { resolveInitialViewState } from '../../lib/geoUtils';
import type { CurrentEvent } from '../events/events_reducers';
import type { DraftState, State } from '../reducers';
import { SimpleSelect } from './editModes';

export interface MapContext {
  mapId: string;
  viewState: ViewState;
  selection: Selection;
  options: Options;
  // Monotonic counter; incrementing it is a "zoom to current selection now"
  // signal the map Editor watches, independent of the current edit mode.
  zoomRequestNonce: number;
  // Monotonic counter; incrementing it is a "fit the viewport to all features now"
  // signal the map Editor watches (same behavior as the global zoom button, e.g.
  // fired after a drag-and-drop import).
  fitAllRequestNonce: number;
  // One-shot signal: id of a freshly created feature whose list panel should
  // open with its Name field focused and text selected. Cleared once consumed.
  pendingNameFocusId: string | null;
  // One-shot signal to play the locator "ping" wave at a location (e.g. when a
  // split is hovered in the elevation chart). The nonce re-triggers the wave even
  // when the same point is pinged repeatedly. Consumed by the map Editor.
  pingRequest: { longitude: number; latitude: number; color?: string; nonce: number } | null;
}

type Options = {
  mapStyle: MapType;
  editMode: EditMode;
};

export type MapsState = {
  maps: {
    ctxs: Map<string, MapContext>;
    currentMapId: null | string;
    getter: {
      currentMapContext: () => Immutable<MapContext> | null;
    };
    actions: {
      setCurrentMapId: (mapId: string | null) => void;
      createMapContext: (
        mapId: string,
        event: Immutable<CurrentEvent>,
        viewportSize?: { width: number; height: number },
      ) => void;
      updateViewState: (mapId: string, newViewport: ViewState) => void;
      updateOptions: (mapId: string, newOptions: Options) => void;
      updateOneOption: (mapId: string, optionName: keyof Options, newValue: any) => void;
      changeEditMode: (mapId: string, editMode: EditMode) => void;
      requestZoomToSelection: (mapId: string) => void;
      requestFitAllFeatures: (mapId: string) => void;
      setPendingNameFocusId: (mapId: string, id: string | null) => void;
      requestPing: (
        mapId: string,
        target: { longitude: number; latitude: number; color?: string },
      ) => void;
      cancelMode: (mapId: string, selecedFeatureIndex: Array<number>) => void;
      updateSelection: (mapId: string, newSelection: Immutable<Selection>) => void;
      updateOneSelection: (mapId: string, selectionName: keyof Selection, newValue: any) => void;
    };
  };
};

export const createMapStore = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Immutable<MapsState> => ({
  maps: {
    ctxs: new Map(),
    currentMapId: null,
    getter: {
      currentMapContext: () => {
        const mapId = get().maps.currentMapId;
        if (mapId == null) return null;

        return get().maps.ctxs.get(mapId) || null;
      },
    },
    actions: {
      setCurrentMapId: (mapId) => {
        set(
          produce((s: DraftState) => {
            s.maps.currentMapId = mapId;
          }),
        );
      },
      createMapContext: (mapId, event, viewportSize) => {
        const features = event.geo.features?.filter(isEditableGeoElement) || [];
        const viewState = resolveInitialViewState({
          customViewport: event.playerOptions.viewport,
          features,
          viewportSize,
        });

        set(
          produce((s: DraftState) => {
            s.maps.ctxs.set(mapId, {
              mapId,
              selection: { featureIds: [] },
              options: {
                editMode: SimpleSelect,
                mapStyle: event.playerOptions.mapType,
              },
              viewState,
              zoomRequestNonce: 0,
              fitAllRequestNonce: 0,
              pendingNameFocusId: null,
              pingRequest: null,
            });
          }),
        );
      },
      updateViewState: (mapId, newViewStat) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.viewState = newViewStat;
          }),
        ),
      updateOptions: (mapId, newOptions) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.options = newOptions;
          }),
        ),
      updateOneOption: (mapId: string, optionName: keyof Options, newValue: any) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.options = {
              ...ctx.options,
              [optionName]: newValue,
            };
          }),
        ),
      changeEditMode: (mapId, editMode) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.options.editMode = editMode;
          }),
        ),
      requestZoomToSelection: (mapId) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.zoomRequestNonce += 1;
          }),
        ),
      requestFitAllFeatures: (mapId) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.fitAllRequestNonce += 1;
          }),
        ),
      setPendingNameFocusId: (mapId, id) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.pendingNameFocusId = id;
          }),
        ),
      requestPing: (mapId, { longitude, latitude, color }) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.pingRequest = {
              longitude,
              latitude,
              color,
              nonce: (ctx.pingRequest?.nonce ?? 0) + 1,
            };
          }),
        ),
      cancelMode: (mapId) => get().maps.actions.changeEditMode(mapId, SimpleSelect),
      updateSelection: (mapId, newSelection) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.selection = Object.assign(newSelection);
          }),
        ),
      updateOneSelection: (mapId, selectionName, newValue) =>
        set(
          produce((state: DraftState) => {
            const ctx = state.maps.ctxs.get(mapId);
            if (ctx == null) return;

            ctx.selection[selectionName] = newValue;
          }),
        ),
    },
  },
});
